import React, { useEffect, useState, useContext } from "react";
import "./ProfileSaya.css";
import axios from "axios";
import { FaUserCircle } from "react-icons/fa";
import { AuthContext } from "../context/AuthContext";
import { authHeaders } from "../utils/authHeaders";

const ENV_API = process.env.REACT_APP_API_URL || "/api";
const CURRENT_ORIGIN = typeof window !== "undefined" ? window.location.origin : "";
const IS_LOCAL_WEB =
  typeof window !== "undefined" && /^(localhost|127\.0\.0\.1)$/i.test(window.location.hostname);
const API_URL = (() => {
  const raw = String(ENV_API || "").trim();
  if (!/^https?:\/\//i.test(raw)) return raw || "/api";
  try {
    const u = new URL(raw);
    const isLocalApi = /^(localhost|127\.0\.0\.1)$/i.test(u.hostname);
    if (!IS_LOCAL_WEB && isLocalApi) return `${CURRENT_ORIGIN}/api`;
    if (typeof window !== "undefined" && window.location.protocol === "https:" && u.protocol === "http:") {
      return raw.replace(/^http:/i, "https:");
    }
    return raw;
  } catch {
    return raw || "/api";
  }
})();
const API_ORIGIN = API_URL.startsWith("http")
  ? API_URL.replace(/\/api\/?$/, "")
  : (CURRENT_ORIGIN || "");

function toDateInput(v) {
  if (!v) return "";
  if (typeof v === "string" && /^\d{4}-\d{2}-\d{2}/.test(v)) return v.slice(0, 10);
  const d = new Date(v);
  if (isNaN(d.getTime())) return "";
  const tz = d.getTimezoneOffset() * 60000;
  const local = new Date(d.getTime() - tz);
  return local.toISOString().slice(0, 10);
}

function toAPIDate(v) {
  if (!v) return "";
  return String(v).slice(0, 10);
}

function resolveMediaUrl(url) {
  const raw = String(url || "").trim();
  if (!raw) return "";
  if (raw.startsWith("blob:")) return "";
  if (raw.startsWith("data:")) return raw;
  if (/^https?:\/\//i.test(raw)) {
    try {
      const u = new URL(raw);
      if (u.pathname.startsWith("/uploads/")) {
        return `${API_URL}${u.pathname}`;
      }
      if (u.pathname.startsWith("/api/uploads/")) {
        return `${API_URL}${u.pathname.replace(/^\/api/, "")}`;
      }
    } catch {
      return raw;
    }
    return raw;
  }
  if (raw.startsWith("/uploads/")) return `${API_URL}${raw}`;
  if (raw.startsWith("/api/uploads/")) return `${API_URL}${raw.replace(/^\/api/, "")}`;
  if (raw.startsWith("/")) return API_ORIGIN ? `${API_ORIGIN}${raw}` : raw;
  return API_ORIGIN ? `${API_ORIGIN}/${raw}` : raw;
}

export default function ProfileSaya() {
  const { user, isAuthLoading } = useContext(AuthContext);

  const [formData, setFormData] = useState({
    profile_pic: "",
    profile_pic_file: null,
    full_name: "",
    gender: "",
    birthdate: "",
    phone: "",
    email: "",
    address: "",
    desa: "",
    kecamatan: "",
    kabupaten: "",
    provinsi: "",
    latitude: "",
    longitude: "",
    farms: 0,
    area: 0,
    facebook_url: "",
    instagram_url: "",
    youtube_url: "",
    tiktok_url: "",
  });
  const [profilePreview, setProfilePreview] = useState("");
  const [editable, setEditable] = useState(false);
  const [backupData, setBackupData] = useState(null);
  const [backupPreview, setBackupPreview] = useState("");

  useEffect(() => {
    if (isAuthLoading || !user?.id) return;

    let mounted = true;
    const fetchProfile = async () => {
      try {
        const res = await axios.get(`${API_URL}/users/${user.id}`, {
          headers: authHeaders(user),
        });
        if (!mounted) return;
        const u = res?.data?.user;
        if (!u) {
          alert("Gagal mengambil data profil");
          return;
        }

        const next = {
          profile_pic: u.profile_pic || "",
          profile_pic_file: null,
          full_name: u.full_name || "",
          gender: u.gender || "",
          birthdate: toDateInput(u.birth_date),
          phone: u.phone || "",
          email: u.email || "",
          address: u.full_address || "",
          desa: u.desa || "",
          kecamatan: u.kecamatan || "",
          kabupaten: u.kabupaten || "",
          provinsi: u.provinsi || "",
          latitude: u.latitude || "",
          longitude: u.longitude || "",
          farms: u.jumlah_kebun || 0,
          area: u.luas_kebun || 0,
          facebook_url: u.facebook_url || "",
          instagram_url: u.instagram_url || "",
          youtube_url: u.youtube_url || "",
          tiktok_url: u.tiktok_url || "",
        };
        setFormData(next);
        setProfilePreview(resolveMediaUrl(next.profile_pic));
      } catch (err) {
        console.error("Error fetching profile:", err);
        alert("Terjadi kesalahan saat mengambil profil.");
      }
    };

    fetchProfile();
    return () => {
      mounted = false;
    };
  }, [isAuthLoading, user]);

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (name === "profile_pic") {
      const file = files?.[0] || null;
      setFormData((prev) => ({ ...prev, profile_pic_file: file }));
      if (profilePreview && profilePreview.startsWith("blob:")) URL.revokeObjectURL(profilePreview);
      setProfilePreview(file ? URL.createObjectURL(file) : resolveMediaUrl(formData.profile_pic));
      return;
    }
    if (name === "birthdate") {
      setFormData((prev) => ({ ...prev, birthdate: toAPIDate(value) }));
      return;
    }
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleEdit = () => {
    setBackupData({ ...formData });
    setBackupPreview(profilePreview);
    setEditable(true);
  };

  const handleCancel = () => {
    if (profilePreview && profilePreview.startsWith("blob:")) URL.revokeObjectURL(profilePreview);
    setFormData(backupData || formData);
    setProfilePreview(backupPreview || resolveMediaUrl(backupData?.profile_pic || ""));
    setEditable(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const requiredFields = ["full_name", "phone", "email", "desa", "kecamatan", "kabupaten", "provinsi"];
    for (const field of requiredFields) {
      if (!formData[field] || String(formData[field]).trim() === "") {
        alert(`Field ${field.replace("_", " ")} wajib diisi.`);
        return;
      }
    }

    try {
      const fd = new FormData();
      fd.append("profile_pic", formData.profile_pic || "");
      if (formData.profile_pic_file) fd.append("profile_pic_file", formData.profile_pic_file);
      fd.append("full_name", formData.full_name);
      fd.append("gender", formData.gender);
      fd.append("birth_date", toAPIDate(formData.birthdate));
      fd.append("phone", formData.phone);
      fd.append("email", formData.email);
      fd.append("full_address", formData.address);
      fd.append("desa", formData.desa);
      fd.append("kecamatan", formData.kecamatan);
      fd.append("kabupaten", formData.kabupaten);
      fd.append("provinsi", formData.provinsi);
      fd.append("latitude", formData.latitude);
      fd.append("longitude", formData.longitude);
      fd.append("jumlah_kebun", formData.farms);
      fd.append("luas_kebun", formData.area);
      fd.append("facebook_url", formData.facebook_url || "");
      fd.append("instagram_url", formData.instagram_url || "");
      fd.append("youtube_url", formData.youtube_url || "");
      fd.append("tiktok_url", formData.tiktok_url || "");

      const response = await axios.put(`${API_URL}/users/${user.id}`, fd, {
        headers: authHeaders(user, { "Content-Type": "multipart/form-data" }),
      });

      if (response?.data?.success) {
        const savedPic = response?.data?.profile_pic || formData.profile_pic || "";
        setFormData((prev) => ({
          ...prev,
          profile_pic: savedPic,
          profile_pic_file: null,
        }));
        if (profilePreview && profilePreview.startsWith("blob:")) URL.revokeObjectURL(profilePreview);
        setProfilePreview(resolveMediaUrl(savedPic));
        alert("Profil berhasil diperbarui!");
        setEditable(false);
      } else {
        alert("Gagal memperbarui profil.");
      }
    } catch (error) {
      console.error("Update error:", error);
      const backendMsg = error?.response?.data?.message || "Terjadi kesalahan saat memperbarui profil.";
      const backendDetail = error?.response?.data?.detail;
      alert(backendDetail ? `${backendMsg}\nDetail: ${backendDetail}` : backendMsg);
    }
  };

  if (isAuthLoading) return <div>Memuat data...</div>;

  return (
    <div className="profile-container">
      <h2>Profil Saya</h2>
      <form className="profile-form" onSubmit={handleSubmit}>
        <div className="profile-top">
          <div className="profile-photo-section">
            {profilePreview ? (
              <img
                src={profilePreview}
                alt="Profil"
                className="profile-photo"
                onError={() => setProfilePreview("")}
              />
            ) : (
              <FaUserCircle className="default-photo" />
            )}
            <input
              type="file"
              name="profile_pic"
              accept="image/*"
              onChange={handleChange}
              disabled={!editable}
            />
          </div>
          <div className="profile-summary">
            <h3>{formData.full_name || "Nama Pengguna"}</h3>
            <p>{formData.email || "-"}</p>
            <small>Kelola data profil, lokasi kebun, dan tautan sosial media Anda.</small>
          </div>
        </div>

        <div className="form-columns">
          <div className="form-left">
            <label>Nama</label>
            <input type="text" name="full_name" value={formData.full_name} onChange={handleChange} disabled={!editable} />

            <label>Jenis Kelamin</label>
            <select name="gender" value={formData.gender} onChange={handleChange} disabled={!editable}>
              <option value="">Pilih</option>
              <option value="Laki-laki">Laki-laki</option>
              <option value="Perempuan">Perempuan</option>
            </select>

            <label>Tanggal Lahir</label>
            <input type="date" name="birthdate" value={formData.birthdate} onChange={handleChange} disabled={!editable} />

            <label>No Handphone</label>
            <input type="text" name="phone" value={formData.phone} onChange={handleChange} disabled={!editable} />

            <label>Email</label>
            <input type="email" name="email" value={formData.email} onChange={handleChange} disabled={!editable} />

            <label>GPS Latitude</label>
            <input type="text" name="latitude" value={formData.latitude} onChange={handleChange} disabled={!editable} />

            <label>GPS Longitude</label>
            <input type="text" name="longitude" value={formData.longitude} onChange={handleChange} disabled={!editable} />
          </div>

          <div className="form-right">
            <label>Alamat Lengkap</label>
            <input type="text" name="address" value={formData.address} onChange={handleChange} disabled={!editable} />

            <label>Desa</label>
            <input type="text" name="desa" value={formData.desa} onChange={handleChange} disabled={!editable} />

            <label>Kecamatan</label>
            <input type="text" name="kecamatan" value={formData.kecamatan} onChange={handleChange} disabled={!editable} />

            <label>Kabupaten</label>
            <input type="text" name="kabupaten" value={formData.kabupaten} onChange={handleChange} disabled={!editable} />

            <label>Provinsi</label>
            <input type="text" name="provinsi" value={formData.provinsi} onChange={handleChange} disabled={!editable} />

            <label>Jumlah Kebun</label>
            <input type="number" name="farms" value={formData.farms} onChange={handleChange} disabled={!editable} />

            <label>Luas Kebun (Ha)</label>
            <input type="number" step="0.1" name="area" value={formData.area} onChange={handleChange} disabled={!editable} />
          </div>
        </div>

        <div className="profile-social">
          <h4>Sosial Media (Opsional)</h4>
          <div className="profile-social-grid">
            <div>
              <label>Facebook</label>
              <input
                type="url"
                name="facebook_url"
                placeholder="https://facebook.com/username"
                value={formData.facebook_url}
                onChange={handleChange}
                disabled={!editable}
              />
            </div>
            <div>
              <label>Instagram</label>
              <input
                type="url"
                name="instagram_url"
                placeholder="https://instagram.com/username"
                value={formData.instagram_url}
                onChange={handleChange}
                disabled={!editable}
              />
            </div>
            <div>
              <label>YouTube</label>
              <input
                type="url"
                name="youtube_url"
                placeholder="https://youtube.com/@channel"
                value={formData.youtube_url}
                onChange={handleChange}
                disabled={!editable}
              />
            </div>
            <div>
              <label>TikTok</label>
              <input
                type="url"
                name="tiktok_url"
                placeholder="https://tiktok.com/@username"
                value={formData.tiktok_url}
                onChange={handleChange}
                disabled={!editable}
              />
            </div>
          </div>
        </div>

        {!editable ? (
          <button type="button" className="btn-update" onClick={handleEdit}>
            Perbarui Profil
          </button>
        ) : (
          <div className="button-group">
            <button type="submit" className="btn-save">Simpan Perubahan</button>
            <button type="button" className="btn-cancel" onClick={handleCancel}>Batal</button>
          </div>
        )}
      </form>
    </div>
  );
}
